import { DEFAULT_DEBOUNCE_TIME } from "@/config";
import { useSuggestUsersQuery } from "@/services";
import { Input, Mentions } from "antd";
import DOMPurify from "dompurify";
import debounce from "lodash.debounce";
import { bool, object } from "prop-types";
import { Fragment, useState } from "react";
import { DynamicFormItem } from "./DynamicFormItem";
import { useCheckEditableFormField } from "./hooks/useCheckEditableFormField";

const { Option, getMentions } = Mentions;

export const DynamicMentions = ({ formField, formDisabled }) => {
  const { isDisabled, formRef } = useCheckEditableFormField(formField);

  const [query, setQuery] = useState();
  const [additionalParameters, setAdditionalParameters] = useState();

  const { data: userSuggestions, isFetching } = useSuggestUsersQuery(
    { query, additionalParameters },
    { skip: !query }
  );

  const onSearch = debounce((searchTerm) => {
    const isInternalComment =
      formRef.current.getFieldValue("isInternal") || false;
    setAdditionalParameters({
      noSpaceLabel: true,
      isInternalComment,
    });
    setQuery(searchTerm);
  }, DEFAULT_DEBOUNCE_TIME);

  const onSelect = ({ key, label, value }, prefix) => {
    const optionData = {
      value: value,
      label: value,
      id: key,
    };
    let curValue = formRef.current.getFieldValue(formField.name);
    const mentions = getMentions(curValue.text);
    let updatedSelected = curValue.mentions ?? [];
    updatedSelected.push(optionData);
    //filter out to just the ones still in the value
    updatedSelected = updatedSelected.filter(
      ({ value }) =>
        mentions.filter(
          (mention) => mention.value === value && mention.prefix === prefix
        ).length
    );
    //update the form state
    curValue = {
      ...curValue,
      mentions: updatedSelected,
    };
    formRef.current.setFieldsValue({ [formField.name]: curValue });
  };

  const textFormField = {
    ...formField,
    name: [formField.name, "text"],
  };
  const mentionFormField = {
    ...formField,
    name: [formField.name, "mentions"],
    requiredForStatuses: undefined,
  };

  return (
    <Fragment>
      <DynamicFormItem formFieldProperty={textFormField.property}>
        <Mentions
          data-test-id={`dynamic-mentions-${formField.property.name}`}
          rows={4}
          disabled={formDisabled || isDisabled}
          loading={isFetching}
          onSearch={onSearch}
          onSelect={onSelect}
        >
          {userSuggestions?.map(({ text, document: { id, displayName } }) => (
            <Option key={id} value={displayName} label={displayName}>
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(text),
                }}
              />
            </Option>
          ))}
        </Mentions>
      </DynamicFormItem>
      {/*This is to make sure the mentions are sent with the form on submit*/}
      <DynamicFormItem formFieldProperty={mentionFormField.property} noStyle>
        <Input style={{ display: "none" }} />
      </DynamicFormItem>
    </Fragment>
  );
};

DynamicMentions.propTypes = {
  formField: object.isRequired,
  formDisabled: bool,
};
